/**
 * faceTrackState — the webcam face tracker's runtime (ENG-1107).
 *
 * THE NODE IS THE SOURCE, exactly as Live Link is. A `faceTracker` node in the
 * face graph with an anim path to Final Pose ("plugged", faceGraphReachability)
 * turns the camera on; unplugging or deleting it turns the camera off. There is
 * no rail button and no second route: wiring the node IS the user's intent,
 * and the permission prompt rides that editor interaction. A blueprint loaded
 * with the node already plugged starts the camera on load — that is what "the
 * node reads the camera feed" means; a viewer who declines lands in `blocked`,
 * the face falls through to LiveLink/clips, and nothing re-prompts until they
 * click Retry (node card or viewport pill). `blocked`/`error` are STICKY under
 * the watcher's re-syncs for the same reason.
 *
 * Module singleton, the sibling of `headTrackedCamera` (ENG-981). The render
 * loop reads `arkit` every frame through `faceTrackWeight()` + the `track` slot
 * in faceFrame.js, so this is NOT React state: `arkit` is a preallocated
 * Float32Array(52) mutated in place at capture cadence (15-30 Hz), never
 * reassigned. The watcher that decides plugged/unplugged runs at UI cadence
 * (250 ms) over faceGraphState — never in the render loop.
 *
 * Settings (`mirror`, `smoothing`) are NODE DATA, Unreal-style inline values,
 * applied live by `configure()`; nothing is persisted per browser.
 *
 * Face lost: the one-euro bank is fed zeros, so the face DECAYS to rest and
 * lands at exactly 0 — never a frozen last expression, never a snap.
 */
import { ARKIT_NAMES } from './arkitNames.js'
import { buildCategoryIndex, applyCategories, mirrorLeftRight } from './mediapipeArkit.js'
import { createOneEuroBank } from './oneEuro.js'
import { pluggedNodeOfType } from './faceGraphReachability.js'
import { faceGraphState } from './faceGraphState.js'
import { devLog } from './devLog.js'

export const FACE_TRACK_SENTINEL = '__facetrack__'
export const FACE_TRACK_NODE_TYPE = 'faceTracker'
export const LANDMARK_COUNT = 478
export const FACE_TRACK_DEFAULTS = { trackFps: 24, mirror: false, smoothing: 0.5 }
const ARKIT_LEN = ARKIT_NAMES.length
const ZEROS = new Float32Array(ARKIT_LEN)
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v)
const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

/** UI smoothing 0..1 -> one-euro minCutoff in Hz: 12 Hz (barely) .. 0.3 Hz (heavy). */
export function smoothingToCutoff(s) {
  return 12 * Math.pow(0.025, clamp01(Number(s) || 0))
}

class FaceTrackState {
  constructor() {
    this._resetForTest()
  }

  // ── the graph contract ────────────────────────────────────────────────
  /** Apply a plugged node's data. Live: smoothing re-tunes the filter, mirror flips the next frame. */
  configure(data) {
    if (!data) return
    if (typeof data.mirror === 'boolean') this.config.mirror = data.mirror
    if (Number.isFinite(data.smoothing)) {
      const s = clamp01(data.smoothing)
      if (s !== this.config.smoothing) { this.config.smoothing = s; this._filter.setMinCutoff(smoothingToCutoff(s)) }
    }
    if (Number.isFinite(data.trackFps) && data.trackFps >= 5 && data.trackFps <= 60) this.config.trackFps = data.trackFps
  }

  /**
   * The ONE arming path. `node` = the plugged faceTracker node (start, or
   * re-configure while running); `null` = nothing plugged (stop, go idle).
   * Idempotent: the watcher calls it every tick.
   */
  syncFromGraph(node) {
    if (!node) {
      if (this.plugged || this.state !== 'idle') this._stop()
      return Promise.resolve()
    }
    this.plugged = true
    this.nodeId = node.id
    this.configure(node.data)
    if (this.state === 'idle') return this._start()
    return Promise.resolve()
  }

  /** User-click path out of blocked/error. A no-op unless a node is plugged. */
  retry() {
    if (!this.plugged) return Promise.resolve()
    if (this.state !== 'blocked' && this.state !== 'error') return Promise.resolve()
    this.state = 'idle' // so _start's guard passes; _setState('starting') notifies next
    return this._start()
  }

  /** Watch the face graph. `getGraph()` returns `{ nodes, edges }` (faceGraphState). */
  bindFaceGraph(getGraph, { intervalMs = 250 } = {}) {
    this._getGraph = getGraph
    if (this._timer) clearInterval(this._timer)
    this._timer = typeof setInterval === 'function' ? setInterval(() => this.tick(), intervalMs) : null
    this.tick()
    return () => {
      if (this._timer) clearInterval(this._timer)
      this._timer = null
      this._getGraph = null
    }
  }

  /** One watcher pass. Synchronous decision; the start it may trigger is async. */
  tick() {
    const g = this._getGraph?.()
    const node = g ? pluggedNodeOfType(FACE_TRACK_NODE_TYPE, g.nodes, g.edges) : null
    this.syncFromGraph(node)
  }

  // ── lifecycle ─────────────────────────────────────────────────────────
  async _start() {
    if (this.state !== 'idle') return
    this._setState('starting')
    const token = (this._startToken = {})
    let facade
    try {
      const factory = this._sessionFactory ?? (async () => (await import('./webcamFaceSession.js')).webcamFaceSession.acquire())
      facade = await factory()
    } catch (err) {
      if (this._startToken !== token) return
      this._handleTrackerError(err)
      return
    }
    if (this._startToken !== token) {
      try { facade.stop() } catch (err) { console.error('[face-track] facade.stop() threw during a superseded start', err) }
      return
    }
    this._facade = facade
    this._filter.reset()
    this._index = null
    try {
      await facade.start({
        trackFps: this.config.trackFps,
        onPose: () => {},
        onFaceLost: () => this._onFaceLost(nowMs()),
        onFaceFrame: (result, tMs) => this._onFaceFrame(result, tMs),
        onError: (err) => this._handleTrackerError(err),
      })
    } catch (err) {
      if (this._startToken !== token) return
      this._handleTrackerError(err)
      return
    }
    if (this._startToken !== token) return
    if (this.state === 'starting') this._setState('tracking')
  }

  _stop() {
    this._startToken = null
    this._releaseFacade()
    this.plugged = false
    this.nodeId = null
    this.faceSeen = false
    this.arkit.fill(0)
    this.raw.fill(0)
    this.gnm = null
    this._setState('idle')
  }

  // ── tracker callbacks (also reached by the test seams) ────────────────
  _onFaceFrame(result, tMs) {
    const categories = result?.faceBlendshapes?.[0]?.categories
    const landmarks = result?.faceLandmarks?.[0]
    if (!categories || !landmarks) { this._onFaceLost(tMs); return }
    if (!this._index || this._index.map.length !== categories.length) {
      this._index = buildCategoryIndex(categories)
      this.unmapped = this._index.missing
      if (this.unmapped.length && !this._warnedMissing) {
        this._warnedMissing = true
        console.error('[face-track] MediaPipe categories missing for ARKit channels:', this.unmapped.join(', '))
      }
    }
    applyCategories(categories, this._index, this.raw)
    if (this.config.mirror) mirrorLeftRight(this.raw)
    this._filter.filter(this.raw, this.arkit, tMs)

    const n = Math.min(landmarks.length, LANDMARK_COUNT)
    const L = this.landmarks
    for (let i = 0; i < n; i++) { const p = landmarks[i]; L[i * 3] = p.x; L[i * 3 + 1] = p.y; L[i * 3 + 2] = p.z }
    this.landmarkCount = n
    const m = result?.facialTransformationMatrixes?.[0]?.data
    this.hasMatrix = !!m
    if (m) for (let i = 0; i < 16; i++) this.matrix[i] = m[i]

    this.frameT = tMs
    this.frames++
    const regained = !this.faceSeen
    this.faceSeen = true
    if (regained) this._notify()
    if (this._enricher) {
      try { this._enricher(this, tMs) } catch (err) { this.gnm = null; console.error('[face-track] frame enricher threw', err) }
    } else {
      this.gnm = null
    }
    this._fireFrame(tMs)
  }

  _onFaceLost(tMs) {
    const lost = this.faceSeen
    this.faceSeen = false
    this._filter.filter(ZEROS, this.arkit, tMs)
    let max = 0
    for (let i = 0; i < ARKIT_LEN; i++) if (this.arkit[i] > max) max = this.arkit[i]
    if (max < 5e-4) this.arkit.fill(0)
    this.landmarkCount = 0
    this.gnm = null
    this.frameT = tMs
    if (lost) this._notify()
    this._fireFrame(tMs)
  }

  _handleTrackerError(err) {
    this._releaseFacade()
    this.faceSeen = false
    this.arkit.fill(0)
    if (err?.name === 'NotAllowedError') {
      this._setState('blocked')
    } else {
      this.errorMessage = err?.message || String(err ?? 'unknown error')
      this._setState('error')
    }
  }

  _releaseFacade() {
    const f = this._facade
    this._facade = null
    if (!f) return
    try { f.stop() } catch (err) { console.error('[face-track] session facade stop() threw', err) }
  }

  // ── listeners ─────────────────────────────────────────────────────────
  onStateChange(cb) { this._listeners.add(cb); return () => this._listeners.delete(cb) }
  /** cb(faceTrackState, tMs) at capture cadence (tracked AND lost ticks). */
  onFrame(cb) { this._frameListeners.add(cb); return () => this._frameListeners.delete(cb) }
  /** ONE synchronous hook run after a frame is written and BEFORE onFrame listeners (Track B's GNM fit). */
  setFrameEnricher(fn) { this._enricher = fn }

  _fireFrame(tMs) {
    for (const fn of this._frameListeners) {
      try { fn(this, tMs) } catch (err) { console.error('[face-track] onFrame listener threw', err) }
    }
  }
  _setState(next) {
    if (this.state === next) return
    this.state = next
    if (next !== 'error') this.errorMessage = null
    devLog('[face-track] state ->', next)
    this._notify()
  }
  _notify() {
    for (const fn of this._listeners) {
      try { fn(this.state) } catch (err) { console.error('[face-track] onStateChange listener threw', err) }
    }
  }

  // ── test seams ────────────────────────────────────────────────────────
  _setSessionFactory(fn) { this._sessionFactory = fn }
  /** One MediaPipe-shaped frame through the SAME handler the tracker calls. */
  _injectFrame({ categories, landmarks, matrix = null, tMs = nowMs() }) {
    this._onFaceFrame({ faceBlendshapes: [{ categories }], faceLandmarks: [landmarks], facialTransformationMatrixes: matrix ? [{ data: matrix }] : [] }, tMs)
  }
  _injectFaceLost(tMs = nowMs()) { this._onFaceLost(tMs) }
  _resetForTest() {
    if (this._facade) this._releaseFacade()
    if (this._timer) clearInterval(this._timer)
    this._timer = null
    this._getGraph = null
    this.state = 'idle'
    this.plugged = false
    this.nodeId = null
    this.faceSeen = false
    this.errorMessage = null
    this.unmapped = []
    this.config = { ...FACE_TRACK_DEFAULTS }
    this.arkit = new Float32Array(ARKIT_LEN)
    this.raw = new Float32Array(ARKIT_LEN)
    this.landmarks = new Float32Array(LANDMARK_COUNT * 3)
    this.landmarkCount = 0
    this.matrix = new Float32Array(16)
    this.hasMatrix = false
    this.frameT = 0
    this.frames = 0
    this.gnm = null
    this._filter = createOneEuroBank(ARKIT_LEN, { minCutoff: smoothingToCutoff(this.config.smoothing), beta: 0.05, dCutoff: 1.0 })
    this._index = null
    this._warnedMissing = false
    this._facade = null
    this._sessionFactory = null
    this._startToken = null
    this._enricher = null
    this._listeners = new Set()
    this._frameListeners = new Set()
  }
}

export const faceTrackState = new FaceTrackState()

/**
 * How much of the tracked frame reaches the face this render frame: the node's
 * evaluated weight while the camera is tracking, nothing otherwise — so a
 * plugged node whose camera is blocked lets the face fall through to
 * LiveLink/clips instead of freezing it at zeros.
 */
export function faceTrackWeight(faceWeights) {
  if (faceTrackState.state !== 'tracking') return 0
  return Math.min(faceWeights?.[FACE_TRACK_SENTINEL] ?? 0, 1)
}

if (typeof window !== 'undefined') {
  window.__FACE_TRACK__ = faceTrackState
  // The graph contract starts with the app: BlueprintEditor and loadBlueprint
  // REPLACE faceGraphState.nodes/edges on every edit and load, so a getter over
  // the module ref sees every change. Browser only — node:test must not start
  // a timer that keeps the process alive.
  faceTrackState.bindFaceGraph(() => faceGraphState)
}
